import type { AxiosRequestConfig } from "axios";
import mitt, { type Emitter } from "mitt";
import { defineStore } from "pinia";
import type { Ref, UnwrapNestedRefs } from "vue";
import { reactive, ref } from "vue";
import { useLanguageStore } from "../../backend/stores/languageStore";
import { api } from "../scripts/api";
import { useFilterStore } from "./filter";

type Events = {
    updatedSearch: { lon: string; lat: string };
    radiusChanged: null;
    locationChanged: null;
};
type LocationOption = {
    label: string | null;
    location: string | null;
};
export const useGeoLocationStore = defineStore("geolocation", () => {
    const languageStore = useLanguageStore();
    const filterStore = useFilterStore();
    const locationPredictions: Ref<LocationOption[]> = ref([]);
    const selectedLocation: Ref<LocationOption> = ref({
        label: sessionStorage.getItem("location_search") ?? "",
        location: sessionStorage.getItem("location_search") ?? "",
    });
    const searchQuery: Ref<string> = ref("");
    const defaultLat: Ref<number> = ref(0);
    const defaultLon: Ref<number> = ref(0);
    const searchLat: Ref<number> = ref(
        sessionStorage.getItem("location_search_lat") ?? 0,
    );
    const searchLon: Ref<number> = ref(
        sessionStorage.getItem("location_search_lon") ?? 0,
    );

    const radiusOptions: Ref<Record<string, string>> = ref({
        "10": "10KM",
        "20": "20KM",
        "50": "50KM",
    });
    const radius: Ref<string> = ref(
        sessionStorage.getItem("location_radius") ?? "0",
    );
    const events: Emitter<Events> = mitt<Events>();

    const autocompleteLocationRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });

    const autocompleteLocations = async (initialSearch) => {
        return new Promise((resolve, reject) => {
            if (autocompleteLocationRequest.abort !== null) {
                autocompleteLocationRequest.abort.abort();
            }

            autocompleteLocationRequest.abort = new AbortController();

            try {
                api()
                    .post(
                        `/site/locations/autocomplete/`,
                        {
                            lang: languageStore.currentIso(),
                            search: searchQuery.value,
                        },
                        {
                            signal: autocompleteLocationRequest.abort.signal,
                        } as AxiosRequestConfig,
                    )
                    .then((response) => {
                        locationPredictions.value = response.data;
                        if (
                            (locationPredictions.value.length === 1 &&
                                initialSearch) ||
                            (locationPredictions.value.length > 0 &&
                                initialSearch)
                        ) {
                            selectLocation(locationPredictions.value[0]);
                        }
                        resolve(response.data);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const initLocationFromQuery = async (location) => {
        return new Promise((resolve, reject) => {
            if (autocompleteLocationRequest.abort !== null) {
                autocompleteLocationRequest.abort.abort();
            }

            autocompleteLocationRequest.abort = new AbortController();

            try {
                api()
                    .post(
                        `/site/locations/autocomplete/`,
                        {
                            lang: languageStore.currentIso(),
                            search: location,
                        },
                        {
                            signal: autocompleteLocationRequest.abort.signal,
                        } as AxiosRequestConfig,
                    )
                    .then((response) => {
                        if (response.data[0]) {
                            selectLocation(response.data[0]);
                            searchQuery.value = response.data[0].label;
                        }
                        resolve(response.data);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const selectLocation = (
        location: LocationOption,
        scrollToOverview: string = "locatie-overzicht",
    ) => {
        selectedLocation.value = location;
        setSearchQuery(location.label, false);
        locationPredictions.value = [];
        if (radius.value === "0" || radius.value === "") {
            setRadius("20", false);
        }

        events.emit("locationChanged");
        document
            .getElementById(scrollToOverview)
            ?.scrollIntoView({ behavior: "smooth" });
    };

    const setSearchQuery = (
        newSearch: string,
        autocomplete: boolean,
        initialSearch: boolean = false,
    ) => {
        sessionStorage.setItem("location_search", newSearch);
        searchQuery.value = newSearch;
        filterStore.setFilterValue("plaats", newSearch, true);

        if (searchQuery.value?.length >= 3 && autocomplete) {
            autocompleteLocations(initialSearch);
        } else if (searchQuery.value.length === 0) {
            setRadius("0");
        }
    };

    const setRadius = (newRadius: string, emit: boolean = true) => {
        radius.value = newRadius;
        sessionStorage.setItem("location_radius", newRadius);
        filterStore.setFilterValue("straal", newRadius, true);

        if (emit) {
            events.emit("radiusChanged");
        }
    };

    const setLatLon = (newLat: number, newLon: number) => {
        searchLat.value = newLat;
        searchLon.value = newLon;
        sessionStorage.setItem("location_search_lat", searchLat.value);
        sessionStorage.setItem("location_search_lon", searchLon.value);

        events.emit("updatedSearch", { lat: newLat, lon: newLon });
    };

    const resetFilters = () => {
        searchQuery.value = "";
        selectedLocation.value = { label: "", location: "" };
        radius.value = "0";

        sessionStorage.removeItem("location_search_lat");
        sessionStorage.removeItem("location_search_lon");
        sessionStorage.removeItem("location_search");
        sessionStorage.removeItem("location_radius");

        filterStore.resetFilters();
    };

    const setStorageFilters = () => {
        if (sessionStorage.getItem("location_search") !== null) {
            setSearchQuery(
                sessionStorage.getItem("location_search") ?? "",
                false,
            );
        }
        if (sessionStorage.getItem("location_radius") !== null) {
            setRadius(sessionStorage.getItem("location_radius"), false);
        }
        if (
            sessionStorage.getItem("location_search_lat") !== null &&
            sessionStorage.getItem("location_search_lon") !== null
        ) {
            setLatLon(
                sessionStorage.getItem("location_search_lat"),
                sessionStorage.getItem("location_search_lon"),
            );
        }
    };

    const getSearchLat = () => {
        return parseFloat(
            sessionStorage.getItem("location_search_lat") ?? defaultLat.value,
        );
    };

    const getSearchLon = () => {
        return parseFloat(
            sessionStorage.getItem("location_search_lon") ?? defaultLon.value,
        );
    };

    return {
        events,
        selectedLocation,
        searchQuery,
        searchLat,
        searchLon,
        locationPredictions,
        radiusOptions,
        radius,
        setRadius,
        resetFilters,
        selectLocation,
        getSearchLat,
        getSearchLon,
        setSearchQuery,
        setLatLon,
        autocompleteLocations,
        initLocationFromQuery,
        setStorageFilters,
    };
});
